import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import ResizableSplitPane from '~/components/Common/Elements/ResizableSplitPane';

import type { OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import InclusionAssetDetailPanel from './InclusionAssetDetailPanel';
import InclusionAssetList from './InclusionAssetList';
import SellableAssetDetailPanel from './SellableAssetDetailPanel';
import { getInclusionAssetView } from './constants';

const PUSHED_FROM_INCLUSION = { fromInclusionAsset: true };

interface Props {
  inclusions?: Array<OpportunityRelatedInclusion>;
  isLoading?: boolean;
}

export default function InclusionAssetsTab({ inclusions = [], isLoading }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedId = searchParams.get('inclusion') ?? undefined;
  const selectedInclusion = inclusions.find((inclusion) => inclusion.id === selectedId) ?? inclusions[0];
  const view = getInclusionAssetView(searchParams.get('view'));

  const updateSearchParams = (
    changes: Record<string, string | null>,
    { replace = false, state }: { replace?: boolean; state?: unknown } = {},
  ) => {
    const nextSearchParams = new URLSearchParams(location.search);

    Object.entries(changes).forEach(([key, value]) => {
      if (value === null) {
        nextSearchParams.delete(key);
      } else {
        nextSearchParams.set(key, value);
      }
    });

    navigate({ pathname: location.pathname, search: nextSearchParams.toString() }, { replace, state });
  };

  // Only opening an Asset pushes history, so Back undoes the drill-down rather than replaying every
  // row a CM clicked through. Selecting also clears the view, or the previous Asset would stay open.
  const handleSelect = (id: string) => updateSearchParams({ inclusion: id, view: null }, { replace: true });
  const handleOpenAsset = () => updateSearchParams({ view: 'asset' }, { state: PUSHED_FROM_INCLUSION });

  // Pop only the entry we pushed. A deep-linked Asset view has none, so popping would leave the page.
  const handleBackToInclusion = () => {
    if ((location.state as typeof PUSHED_FROM_INCLUSION | null)?.fromInclusionAsset) {
      navigate(-1);
      return;
    }

    updateSearchParams({ view: null }, { replace: true });
  };

  if (isLoading) {
    return null;
  }

  if (!inclusions.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No inclusion assets yet. Add inclusions in the Hotel Proposal pricing tool and they sync here automatically.
        </Typography>
      </Box>
    );
  }

  return (
    <ResizableSplitPane storageKey="offerBuilderInclusionAssetsSplit" defaultLeftWidth={280}>
      <InclusionAssetList inclusions={inclusions} selectedId={selectedInclusion?.id} onSelect={handleSelect} />
      {view === 'asset' && selectedInclusion?.asset ? (
        <SellableAssetDetailPanel asset={selectedInclusion.asset} onBack={handleBackToInclusion} />
      ) : (
        <InclusionAssetDetailPanel inclusion={selectedInclusion} onOpenAsset={handleOpenAsset} />
      )}
    </ResizableSplitPane>
  );
}
